import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Search, Ban, Gift, Trash2, Settings,
  CheckCircle, XCircle, ArrowLeft, Loader2, HardDrive, FileBox
} from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useAuthStore } from '../store/auth-store';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [searchEmail, setSearchEmail] = useState('');
  const [targetUser, setTargetUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [customStorage, setCustomStorage] = useState('');
  const [customNotes, setCustomNotes] = useState('');

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user || user.email !== 'knownasjunaid@gmail.com') {
        navigate('/403');
        return;
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (error || !data?.is_admin) {
        navigate('/403');
        return;
      }

      setIsAdmin(true);
    };

    checkAdmin();
  }, [user, navigate]);

  const handleSearch = async () => {
    if (!searchEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsLoading(true);
    try {
      const { data: userData, error: userError } = await supabase
        .from('auth.users')
        .select('*, user_settings:user_settings(*)')
        .eq('email', searchEmail.trim())
        .single();

      if (userError) throw userError;

      setTargetUser(userData);
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error('User not found');
      setTargetUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSubscription = async (plan: string) => {
    if (!targetUser) return;
    
    try {
      const expiryDate = new Date();
      expiryDate.setMonth(expiryDate.getMonth() + 1);

      const { error } = await supabase
        .from('user_settings')
        .update({ 
          subscription: plan,
          subscription_expiry: expiryDate.toISOString()
        })
        .eq('id', targetUser.id);

      if (error) throw error;
      
      toast.success('Subscription updated successfully');
      handleSearch(); // Refresh user data
    } catch (error: any) {
      toast.error('Failed to update subscription');
    }
  };

  const handleToggleBan = async () => {
    if (!targetUser) return;
    
    try {
      const { error } = await supabase
        .from('user_settings')
        .update({ 
          is_banned: !targetUser.user_settings.is_banned 
        })
        .eq('id', targetUser.id);

      if (error) throw error;
      
      toast.success(`User ${targetUser.user_settings.is_banned ? 'unbanned' : 'banned'} successfully`);
      handleSearch(); // Refresh user data
    } catch (error: any) {
      toast.error('Failed to update ban status');
    }
  };

  const handleDeleteAccount = async () => {
    if (!targetUser) return;
    
    try {
      // Delete user settings first (this will cascade to other tables)
      const { error: settingsError } = await supabase
        .from('user_settings')
        .delete()
        .eq('id', targetUser.id);

      if (settingsError) throw settingsError;

      // Delete auth user
      const { error: authError } = await supabase.auth.admin.deleteUser(targetUser.id);
      
      if (authError) throw authError;

      toast.success('Account deleted successfully');
      setTargetUser(null);
    } catch (error: any) {
      toast.error('Failed to delete account');
    }
  };

  const handleUpdateLimits = async () => {
    if (!targetUser) return;
    
    try {
      const updates: any = {};
      
      if (customStorage) {
        updates.storage_limit = parseInt(customStorage) * 1024 * 1024; // Convert MB to bytes
      }
      
      if (customNotes) {
        updates.notes_limit = parseInt(customNotes);
      }

      const { error } = await supabase
        .from('user_settings')
        .update(updates)
        .eq('id', targetUser.id);

      if (error) throw error;
      
      toast.success('Custom limits updated successfully');
      handleSearch(); // Refresh user data
      setCustomStorage('');
      setCustomNotes('');
    } catch (error: any) {
      toast.error('Failed to update limits');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-purple-500/5">
      <header className="sticky top-0 border-b bg-background/80 backdrop-blur-sm z-40">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-semibold">Admin Dashboard</h1>
          <div className="w-10" />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-semibold">User Management</h2>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Enter user email..."
                value={searchEmail}
                onChange={(e) => setSearchEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button onClick={handleSearch} disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </Button>
          </div>

          {targetUser && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              <div className="rounded-lg border bg-card p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold">
                      {targetUser.user_metadata?.name || 'Unnamed User'}
                    </h3>
                    <p className="text-sm text-muted-foreground">{targetUser.email}</p>
                  </div>
                  {targetUser.user_settings?.is_banned ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-destructive/10 text-destructive">
                      <Ban className="w-3 h-3 mr-1" />
                      Banned
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Active
                    </span>
                  )}
                </div>

                <div className="grid gap-6">
                  {/* Subscription Management */}
                  <div>
                    <h4 className="text-sm font-medium mb-4">Subscription</h4>
                    <div className="flex gap-2">
                      <Button
                        variant={targetUser.user_settings?.subscription === 'free' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSubscription('free')}
                        className="flex-1"
                      >
                        Free
                      </Button>
                      <Button
                        variant={targetUser.user_settings?.subscription === 'pro' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSubscription('pro')}
                        className="flex-1"
                      >
                        Pro
                      </Button>
                      <Button
                        variant={targetUser.user_settings?.subscription === 'premium' ? 'default' : 'outline'}
                        onClick={() => handleUpdateSubscription('premium')}
                        className="flex-1"
                      >
                        Premium
                      </Button>
                    </div>
                  </div>

                  {/* Custom Limits */}
                  <div>
                    <h4 className="text-sm font-medium mb-4">Custom Limits</h4>
                    <div className="grid gap-4">
                      <div>
                        <label className="block text-sm text-muted-foreground mb-2">
                          Storage Limit (MB)
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <HardDrive className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              value={customStorage}
                              onChange={(e) => setCustomStorage(e.target.value)}
                              className="pl-9"
                              placeholder="Enter storage limit in MB"
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-2">
                          Notes Limit
                        </label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <FileBox className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              value={customNotes}
                              onChange={(e) => setCustomNotes(e.target.value)}
                              className="pl-9"
                              placeholder="Enter maximum notes allowed"
                            />
                          </div>
                        </div>
                      </div>
                      <Button onClick={handleUpdateLimits}>
                        Update Limits
                      </Button>
                    </div>
                  </div>

                  {/* Account Actions */}
                  <div>
                    <h4 className="text-sm font-medium mb-4">Account Actions</h4>
                    <div className="flex gap-2">
                      <Button
                        variant={targetUser.user_settings?.is_banned ? 'default' : 'destructive'}
                        onClick={handleToggleBan}
                        className="flex-1"
                      >
                        {targetUser.user_settings?.is_banned ? (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Unban User
                          </>
                        ) : (
                          <>
                            <Ban className="w-4 h-4 mr-2" />
                            Ban User
                          </>
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={handleDeleteAccount}
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Account
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;